import React, { useState, useEffect } from 'react';
import { Badge, Card, Typography, Space, Button, Tabs } from 'antd';
import { WifiOutlined, DisconnectOutlined, SyncOutlined, CheckCircleOutlined, ExclamationCircleOutlined, BugOutlined } from '@ant-design/icons';
import { useSignalR } from '../Context/ChatContext';
import { getConnectionState, setSignalRLogger } from '../utils/SignalRUtils';
import { runQuickDiagnostics } from '../utils/SignalRDiagnostics';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

const SignalRMonitor = () => {
  const { t } = useTranslation();
  const chatConnection = useSignalR();
  const [mainHubConnection, setMainHubConnection] = useState(null);
  const [mainHubState, setMainHubState] = useState('Disconnected');
  const [chatHubState, setChatHubState] = useState('Disconnected');
  const [lastPing, setLastPing] = useState(null);
  const [errorCount, setErrorCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [logs, setLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('chathub');

  // Add log to state
  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const newLog = { message, type, timestamp };
    setLogs(prev => [...prev.slice(-49), newLog]); // Keep last 50 logs
  };

  // Set up SignalR logger
  useEffect(() => {
    setSignalRLogger(addLog);
    return () => setSignalRLogger(null);
  }, []);

  // Monitor ChatHub connection
  useEffect(() => {
    if (chatConnection) {
      setChatHubState(getConnectionState(chatConnection));
      
      // Monitor connection state changes
      const checkState = () => {
        setChatHubState(getConnectionState(chatConnection));
      };

      const interval = setInterval(checkState, 1000);
      
      return () => clearInterval(interval);
    }
  }, [chatConnection]);

  // Monitor MainHub connection (from SmartMap)
  useEffect(() => {
    // Try to find MainHub connection from window object or global state
    const checkMainHubConnection = () => {
      // Look for MainHub connection in various places
      const possibleConnections = [
        window.mainHubConnection,
        window.signalrConnection,
        window.mapConnection,
        window.signalRConnection
      ];

      for (const conn of possibleConnections) {
        if (conn && conn.state !== undefined) {

          setMainHubConnection(conn);
          setMainHubState(getConnectionState(conn));
          return;
        }
      }

      // If no connection found, check if we're on SmartMap page
      if (window.location.pathname.includes('smart-map') || window.location.pathname.includes('map')) {
        setMainHubState(t('signalRMonitor.checking'));
      } else {
        setMainHubState(t('signalRMonitor.notAvailable'));
      }
    };

    const interval = setInterval(checkMainHubConnection, 2000);
    checkMainHubConnection(); // Initial check

    return () => clearInterval(interval);
  }, [t]);

  // Also listen for connection state changes when connection is found
  useEffect(() => {
    if (mainHubConnection) {
      const checkState = () => {
        setMainHubState(getConnectionState(mainHubConnection));
      };

      const interval = setInterval(checkState, 1000);
      
      return () => clearInterval(interval);
    }
  }, [mainHubConnection]);

  const getStatusColor = (state) => {
    // Compare against actual connection state strings, not translated ones
    switch (state) {
      case 'Connected':
        return 'success';
      case 'Connecting':
      case 'Reconnecting':
      case 'Checking...':
        return 'processing';
      case 'Disconnected':
      case 'Not Available':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (state) => {
    // Compare against actual connection state strings, not translated ones
    switch (state) {
      case 'Connected':
        return <CheckCircleOutlined />;
      case 'Connecting':
      case 'Reconnecting':
      case 'Checking...':
        return <SyncOutlined spin />;
      case 'Disconnected':
      case 'Not Available':
        return <DisconnectOutlined />;
      default:
        return <ExclamationCircleOutlined />;
    }
  };

  const formatTime = (date) => {
    if (!date) return t('signalRMonitor.never');
    return date.toLocaleTimeString();
  };

  const MainHubStatus = () => (
    <Space direction="vertical" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text>{t('signalRMonitor.mainhub')} {t('signalRMonitor.status')}:</Text>
        <Badge
          status={getStatusColor(mainHubState)}
          text={
            <Space>
              {getStatusIcon(mainHubState)}
              <Text strong>{mainHubState}</Text>
            </Space>
          }
        />
      </div>

      {mainHubConnection && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text>{t('signalRMonitor.connectionId')}:</Text>
          <Text code style={{ fontSize: '10px' }}>
            {mainHubConnection.connectionId || 'N/A'}
          </Text>
        </div>
      )}

      {mainHubState === 'Not Available' && (
        <div style={{ fontSize: '11px', color: '#999', textAlign: 'center' }}>
          {t('signalRMonitor.mainhubNotAvailable')}
        </div>
      )}
    </Space>
  );

  const ChatHubStatus = () => (
    <Space direction="vertical" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text>{t('signalRMonitor.chathub')} {t('signalRMonitor.status')}:</Text>
        <Badge
          status={getStatusColor(chatHubState)}
          text={
            <Space>
              {getStatusIcon(chatHubState)}
              <Text strong>{chatHubState}</Text>
            </Space>
          }
        />
      </div>

      {chatConnection && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text>{t('signalRMonitor.connectionId')}:</Text>
          <Text code style={{ fontSize: '10px' }}>
            {chatConnection.connectionId || 'N/A'}
          </Text>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text>{t('signalRMonitor.lastPing')}:</Text>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {formatTime(lastPing)}
        </Text>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text>{t('signalRMonitor.errors')}:</Text>
        <Badge count={errorCount} style={{ backgroundColor: errorCount > 0 ? '#ff4d4f' : '#52c41a' }} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text>{t('signalRMonitor.timeout')}:</Text>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {chatConnection?.serverTimeoutInMilliseconds ? `${chatConnection.serverTimeoutInMilliseconds / 1000}s` : 'N/A'}
        </Text>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text>{t('signalRMonitor.keepAlive')}:</Text>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {chatConnection?.keepAliveIntervalInMilliseconds ? `${chatConnection.keepAliveIntervalInMilliseconds / 1000}s` : 'N/A'}
        </Text>
      </div>
    </Space>
  );

  const LogsCard = () => (
    <Space direction="vertical" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text>{t('signalRMonitor.logsCount', { count: logs.length })}</Text>
        <Button 
          size="small" 
          onClick={() => setLogs([])}
          disabled={logs.length === 0}
        >
          {t('signalRMonitor.clear')}
        </Button>
      </div>
      
      <div 
        style={{ 
          maxHeight: 200, 
          overflowY: 'auto', 
          border: '1px solid #d9d9d9', 
          borderRadius: 4,
          padding: 8,
          backgroundColor: '#fafafa',
          fontSize: '11px'
        }}
      >
        {logs.length === 0 ? (
          <Text type="secondary" style={{ fontSize: '11px' }}>{t('signalRMonitor.noLogsYet')}</Text>
        ) : (
          logs.map((log, index) => (
            <div key={index} style={{ marginBottom: 4 }}>
              <Text type="secondary" style={{ fontSize: '9px' }}>[{log.timestamp}]</Text>
              <Text 
                style={{ 
                  fontSize: '10px',
                  color: log.type === 'error' ? '#ff4d4f' : 
                         log.type === 'warning' ? '#faad14' : '#1890ff'
                }}
              >
                {log.message}
              </Text>
            </div>
          ))
        )}
      </div>
    </Space>
  );

  // Define tab items for the new Tabs API
  const tabItems = [
    {
      key: 'chathub',
      label: t('signalRMonitor.chathub'),
      children: <ChatHubStatus />
    },
    {
      key: 'mainhub',
      label: t('signalRMonitor.mainhub'),
      children: <MainHubStatus />
    },
    {
      key: 'logs',
      label: t('signalRMonitor.logs'),
      children: <LogsCard />
    }
  ];

  if (!isVisible) {
    return (
      <Button
        type="text"
        icon={<WifiOutlined />}
        onClick={() => setIsVisible(true)}
        style={{
          position: 'fixed',
          bottom: 50,
          right: 50,
          zIndex: 1000,
          background: '#fff',
          border: '1px solid #d9d9d9',
          borderRadius: '50%',
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      />
    );
  }

  return (
    <Card
      size="small"
      title={
        <Space>
          <WifiOutlined />
          <Text strong>{t('signalRMonitor.title')}</Text>
        </Space>
      }
      extra={
        <Button
          type="text"
          size="small"
          onClick={() => setIsVisible(false)}
        >
          ×
        </Button>
      }
      style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 1000,
        width: 350,
        maxHeight: 500,
        overflow: 'auto'
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} size="small" items={tabItems} />

        <Button
          size="small"
          onClick={() => {
            setErrorCount(0);
            setLastPing(null);
          }}
          style={{ width: '100%' }}
        >
          {t('signalRMonitor.resetCounters')}
        </Button>

        <Button
          size="small"
          icon={<BugOutlined />}
          onClick={async () => {
            setActiveTab('logs');
            setTimeout(() => addLog(t('signalRMonitor.diagnosticsRunning'), 'info'), 0);
            try {
              await runQuickDiagnostics();
              setTimeout(() => addLog(t('signalRMonitor.diagnosticsCompleted'), 'info'), 0);
            } catch (error) {
              setTimeout(() => addLog(`${t('signalRMonitor.diagnosticsFailed')} ${error.message}`, 'error'), 0);
            }
          }}
          style={{ width: '100%', marginTop: 8 }}
        >
          {t('signalRMonitor.runDiagnostics')}
        </Button>
      </Space>
    </Card>
  );
};

export default SignalRMonitor; 